/**
 * Jours fériés français (11 par an).
 *
 * Contient des dates fixes (Jour de l'An, 1er mai, etc.) et des dates mobiles
 * qui dépendent de Pâques (Lundi de Pâques, Ascension, Pentecôte).
 *
 * Les dates sont calculées à la volée par année et mises en cache pour
 * éviter de recalculer Pâques à chaque appel.
 */

/** Cache : année → Set<dateKey> des jours fériés. */
const cache = new Map<number, Set<string>>();

/**
 * Algorithme de Butcher (Gauss amélioré) pour calculer la date de Pâques
 * d'une année donnée. Précis pour le calendrier grégorien (à partir de 1583).
 */
function easterDate(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 3 (mars) ou 4 (avril)
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function dateKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function buildHolidaysForYear(year: number): Set<string> {
  const easter = easterDate(year);
  const dates: Date[] = [
    new Date(year, 0, 1), // Jour de l'An
    addDays(easter, 1), // Lundi de Pâques
    new Date(year, 4, 1), // 1er mai — Fête du Travail
    new Date(year, 4, 8), // 8 mai — Victoire 1945
    addDays(easter, 39), // Jeudi de l'Ascension
    addDays(easter, 50), // Lundi de Pentecôte
    new Date(year, 6, 14), // 14 juillet — Fête Nationale
    new Date(year, 7, 15), // 15 août — Assomption
    new Date(year, 10, 1), // 1er novembre — Toussaint
    new Date(year, 10, 11), // 11 novembre — Armistice
    new Date(year, 11, 25), // 25 décembre — Noël
  ];
  return new Set(dates.map(dateKey));
}

/** Retourne `true` si la date est un jour férié français (Alsace-Moselle ignorée). */
export function isFrenchHoliday(d: Date): boolean {
  const year = d.getFullYear();
  let set = cache.get(year);
  if (!set) {
    set = buildHolidaysForYear(year);
    cache.set(year, set);
  }
  return set.has(dateKey(d));
}

/** Retourne `true` si c'est un weekend (samedi ou dimanche). */
export function isWeekend(d: Date): boolean {
  const day = d.getDay();
  return day === 0 || day === 6;
}

/** Weekend OU jour férié FR. */
export function isOffDay(d: Date): boolean {
  return isWeekend(d) || isFrenchHoliday(d);
}
